import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import axios from 'axios';
import './GuildWarInstructions.css';
import debounce from 'lodash/debounce';
import { API_BASE_URL } from '../config/api';
import DefenderDetailModal from './DefenderDetailModal';

// StarRatingコンポーネントをグローバルに定義
const StarRating = ({ towerId, userIndex, currentRating, onRatingChange }) => {
  const maxStars = 5;

  const getStarClass = (index, rating) => {
    if (!rating) return '';
    if (index >= rating) return '';

    let classes = 'filled';
    if (rating >= 5) classes += ' extreme';
    else if (rating >= 4) classes += ' very-high';
    else if (rating >= 3) classes += ' high';
    return classes;
  };

  const handleStarClick = (index) => {
    const newRating = index + 1;
    onRatingChange(newRating);
  };

  return (
    <div className="star-rating">
      {[...Array(maxStars)].map((_, index) => (
        <span
          key={index}
          className={`star ${getStarClass(index, currentRating)}`}
          onClick={() => handleStarClick(index)}
        >
          ★
        </span>
      ))}
    </div>
  );
};

const GuildWarInstructions = () => {
  // 認証関連の状態
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // タブ関連の状態
  const [activeTab, setActiveTab] = useState('instructionManagement'); // 'instructionManagement' or 'battleStatus'
  const [instructionSubTab, setInstructionSubTab] = useState('instructions'); // 'instructions' or 'enemy'
  const [battleStatusSubTab, setBattleStatusSubTab] = useState('battleMap'); // 'battleMap' or 'attackStatus'
  const [attackStatusView, setAttackStatusView] = useState('ally'); // 'enemy' or 'ally' for attackStatus内部view
  const [battleMapView, setBattleMapView] = useState('ally'); // 'ally' or 'opponent' for battleMap内部view

  // 対戦状況の状態
  const [battleStatusData, setBattleStatusData] = useState({
    ally: { 1: { towers: {} }, 2: { towers: {} }, 3: { towers: {} } },
    opponent: { 1: { towers: {} }, 2: { towers: {} }, 3: { towers: {} } }
  });

  // 選択された塔の状態
  const [selectedTowerModal, setSelectedTowerModal] = useState(null);

  // タワー構造の定義
  const towerStructure = {
    floor1: {
      towers: [1, 2, 3, 4, 5, 6],
      maxAttackers: 2
    },
    floor2: {
      towers: [7, 8, 9],
      maxAttackers: 3
    },
    floor3: {
      towers: [10],
      maxAttackers: 4
    }
  };

  // グループの定義を追加
  const groups = ['防衛配置外&時間ない人', '防衛配置&挑戦したい方', '全員'];

  // アラートを出すバフのしきい値
  const BUFF_ALERT_THRESHOLD = 2;

  // 指示タブの状態を更新
  const [towerStatus, setTowerStatus] = useState({});
  const [attackOrders, setAttackOrders] = useState({});
  // バージョン管理用の状態を追加
  const [instructionsVersion, setInstructionsVersion] = useState(1);
  // dirtyフラグを追加
  const [isDirty, setIsDirty] = useState(false);

  // 敵タブの状態
  const [enemyTowers, setEnemyTowers] = useState([]);
  const [userRatings, setUserRatings] = useState({});

  // 味方タブの状態
  const [allyStatus, setAllyStatus] = useState({
    1: { completed: [], pending: [], partialAttack: [] },
    2: { completed: [], pending: [], partialAttack: [] },
    3: { completed: [], pending: [], partialAttack: [] }
  });
  const [updateMessage, setUpdateMessage] = useState('');

  // 新しい状態を追加
  const [showIndividualSettings, setShowIndividualSettings] = useState({});
  const [sortByOrder, setSortByOrder] = useState(false);

  // 戦闘選択の状態を追加
  const [selectedBattle, setSelectedBattle] = useState(1);
  const [battleUpdateMessage, setBattleUpdateMessage] = useState('');

  // 敵タブの状態を戦闘ごとに管理
  const [enemyTowersPerBattle, setEnemyTowersPerBattle] = useState({
    1: {},
    2: {},
    3: {}
  });
  const [userRatingsPerBattle, setUserRatingsPerBattle] = useState({
    1: {},
    2: {},
    3: {}
  });

  // 敵の攻撃状況の状態
  const [enemyAttackStatus, setEnemyAttackStatus] = useState({
    1: { completed: [], pending: [], partialAttack: [] },
    2: { completed: [], pending: [], partialAttack: [] },
    3: { completed: [], pending: [], partialAttack: [] }
  });

  // バフレベルのオプションを定義
  const buffLevels = [2, 4, 6, 8, 10];

  // 評価の更新処理を追加
  const handleUserRating = async (username, rating) => {
    try {
      const newRatings = { ...userRatings, [username]: rating };
      await saveEnemyTowers({
        towers: enemyTowers,
        userRatings: newRatings,
        battleNumber: selectedBattle
      });
      setUserRatings(newRatings);
      setIsDirty(true);
    } catch (error) {
      console.error('評価の保存に失敗しました:', error);
      alert('評価の保存に失敗しました。もう一度お試しください。');
    }
  };

  const saveEnemyTowers = async ({ towers, userRatings, battleNumber }) => {
    try {
      const strippedTowers = {};
      Object.entries(towers).forEach(([id, members]) => {
        strippedTowers[id] = (Array.isArray(members) ? members : []).map(m => {
          if (typeof m === 'object' && m !== null) {
            const { deckInfoJson, mergedGuildPlayer, ...rest } = m;
            return rest;
          }
          return m;
        });
      });
      await axios.post(
        `${API_BASE_URL}/api/guild-war-instructions-nihon/save-enemy-towers`,
        {
          towers: strippedTowers,
          userRatings,
          battleNumber
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );
    } catch (error) {
      console.error('敵タワー情報の保存に失敗しました:', error);
      throw error;
    }
  };

  // データ取得用のAPI関数
  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      console.log('初期データの取得を開始します');
      const [instructionsResponse, enemyTowersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/guild-war-instructions-nihon/instructions?battleNumber=${selectedBattle}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        }),
        axios.get(`${API_BASE_URL}/api/guild-war-instructions-nihon/enemy-towers?battleNumber=${selectedBattle}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        })
      ]);

      console.log('指示データ:', instructionsResponse.data);
      console.log('敵タワーデータ:', enemyTowersResponse.data);

      if (instructionsResponse.data) {
        setTowerStatus(instructionsResponse.data.instructions || {});
        setInstructionsVersion(instructionsResponse.data.version || 1);
      }

      if (enemyTowersResponse.data) {
        setEnemyTowers(enemyTowersResponse.data.towers || {});
        setUserRatings(enemyTowersResponse.data.userRatings || {});

        // 敵ギルドのgidが取得できていれば、敵の攻撃状況も取得
        if (enemyTowersResponse.data.gid) {
          fetchEnemyAttackStatus(enemyTowersResponse.data.gid);
        }
      }

      console.log('初期データの取得が完了しました');
    } catch (error) {
      console.error('初期データの取得に失敗しました:', error);
      console.error('エラーの詳細:', error.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  // 敵の攻撃状況を取得する関数
  const fetchEnemyAttackStatus = async (gid) => {
    if (!gid) return;

    try {
      console.log(`敵の${selectedBattle}戦目の攻撃状況を取得します`);

      // 必要なバトル番号を決定
      // 2戦目なら1戦目のデータも、3戦目なら2戦目のデータも必要
      const battleNumbersToFetch = [selectedBattle];
      if (selectedBattle > 1) {
        battleNumbersToFetch.unshift(selectedBattle - 1);
      }

      const promises = battleNumbersToFetch.map(battleNumber =>
        axios.get(
          `${API_BASE_URL}/api/guild-war-instructions-nihon/attack-status/${gid}/${battleNumber}`,
          {
            headers: { Authorization: `Bearer ${authToken}` }
          }
        ).catch(error => {
          console.error(`敵の${battleNumber}戦目の状態取得に失敗:`, error);
          return { data: { completed: [], pending: [], partialAttack: [] } };
        })
      );

      const responses = await Promise.all(promises);

      // 状態の更新
      const updatedStatus = { ...enemyAttackStatus };
      battleNumbersToFetch.forEach((battleNumber, index) => {
        updatedStatus[battleNumber] = responses[index].data || { completed: [], pending: [], partialAttack: [] };
      });

      setEnemyAttackStatus(updatedStatus);

      console.log('敵の攻撃状況の取得が完了しました');
    } catch (error) {
      console.error('敵の攻撃状況の取得に失敗しました:', error);
    }
  };

  // 外部APIから新しいデータを取得してRedisに保存
  const fetchAndSaveAllyStatus = async () => {
    try {
      setUpdateMessage('データを更新中...');
      // contributionを取得
      const contributionResponse = await axios.get(
        `${API_BASE_URL}/api/guild-war-instructions-nihon/contribution/d1431c50f9d963b8d66f195b156e7db8`,
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      if (contributionResponse.data) {
        // contributionのレスポンスが成功したら、すぐにattack-statusを取得
        const allyStatusPromises = [1, 2, 3].map(battleNumber =>
          axios.get(
            `${API_BASE_URL}/api/guild-war-instructions-nihon/attack-status/d1431c50f9d963b8d66f195b156e7db8/${battleNumber}`,
            {
              headers: { Authorization: `Bearer ${authToken}` }
            }
          )
        );

        const [response1, response2, response3] = await Promise.all(allyStatusPromises);

        setAllyStatus({
          1: {
            completed: response1.data?.completed || [],
            pending: response1.data?.pending || [],
            partialAttack: response1.data?.partialAttack || []
          },
          2: {
            completed: response2.data?.completed || [],
            pending: response2.data?.pending || [],
            partialAttack: response2.data?.partialAttack || []
          },
          3: {
            completed: response3.data?.completed || [],
            pending: response3.data?.pending || [],
            partialAttack: response3.data?.partialAttack || []
          }
        });

        setUpdateMessage('✅ 更新完了');
        setTimeout(() => setUpdateMessage(''), 3000);
      }
    } catch (error) {
      console.error('データ取得に失敗:', error);
      setUpdateMessage('❌ 更新失敗');
      setTimeout(() => setUpdateMessage(''), 3000);
    }
  };

  // 指示情報の保存
  const saveInstructions = async (data) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/guild-war-instructions-nihon/save-instructions`,
        {
          ...data,
          version: instructionsVersion
        },
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );
      if (res.data.version) {
        setInstructionsVersion(res.data.version);
      }
      setIsDirty(false); // 保存成功後にdirty解除
      console.log('保存が完了しました');
    } catch (error) {
      if (error.response?.data?.error === 'CONFLICT') {
        alert('他のユーザーが先に編集しています。ページをリロードしてください。');
        setTowerStatus(error.response.data.current.instructions || {});
        setInstructionsVersion(error.response.data.current.version || 1);
        setIsDirty(false); // 競合時もdirty解除
      } else {
        console.error('指示の保存に失敗しました:', error);
      }
    }
  };

  // 認証処理
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/verify-password`,
        { password }
      );

      if (response.data.success) {
        const token = response.data.token;
        localStorage.setItem('authTokenRaidSkill', token);
        setAuthToken(token);
        setIsAuthenticated(true);
      } else {
        setAuthError('パスワードが間違っています');
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError('パスワードが間違っているか、サーバーに接続できません');
    } finally {
      setIsLoading(false);
      setPassword(''); // パスワードをクリア
    }
  };

  // トークンの検証
  const verifyToken = async (token) => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/verify-token`,
        { token }
      );

      if (response.data.success) {
        setIsAuthenticated(true);
        setAuthToken(token);
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error('Token verification error:', error);
      handleLogout();
    } finally {
      setIsLoading(false);
    }
  };

  // 初期化時にデータを取得
  useEffect(() => {
    const storedToken = localStorage.getItem('authTokenRaidSkill');
    if (storedToken) {
      verifyToken(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  // 全ての戦闘の攻撃状況を自動取得する関数
  const fetchAllBattleStatus = async () => {
    try {
      console.log('全ての戦闘の攻撃状況を取得開始');

      // まず味方のcontributionを取得
      await axios.get(
        `${API_BASE_URL}/api/guild-war-instructions-nihon/contribution/d1431c50f9d963b8d66f195b156e7db8`,
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      // 1-3戦目全ての味方の攻撃状況を取得
      const allyStatusPromises = [1, 2, 3].map(battleNumber =>
        axios.get(
          `${API_BASE_URL}/api/guild-war-instructions-nihon/attack-status/d1431c50f9d963b8d66f195b156e7db8/${battleNumber}`,
          {
            headers: { Authorization: `Bearer ${authToken}` }
          }
        ).catch(error => {
          console.error(`${battleNumber}戦目の味方の攻撃状況取得に失敗:`, error);
          return { data: { completed: [], pending: [], partialAttack: [] } };
        })
      );

      const [response1, response2, response3] = await Promise.all(allyStatusPromises);

      setAllyStatus({
        1: response1.data || { completed: [], pending: [], partialAttack: [] },
        2: response2.data || { completed: [], pending: [], partialAttack: [] },
        3: response3.data || { completed: [], pending: [], partialAttack: [] }
      });

      // 1-3戦目全ての敵タワー情報を取得して、敵の攻撃状況も取得
      const enemyStatusPromises = [1, 2, 3].map(async battleNumber => {
        try {
          const enemyTowersResponse = await axios.get(
            `${API_BASE_URL}/api/guild-war-instructions-nihon/enemy-towers?battleNumber=${battleNumber}`,
            {
              headers: { Authorization: `Bearer ${authToken}` }
            }
          );

          if (enemyTowersResponse.data && enemyTowersResponse.data.gid) {
            const gid = enemyTowersResponse.data.gid;

            // 敵のcontributionを取得
            await axios.get(
              `${API_BASE_URL}/api/guild-war-instructions-nihon/contribution/${gid}`,
              {
                headers: { Authorization: `Bearer ${authToken}` }
              }
            );

            // 敵の攻撃状況を取得
            const attackStatusResponse = await axios.get(
              `${API_BASE_URL}/api/guild-war-instructions-nihon/attack-status/${gid}/${battleNumber}`,
              {
                headers: { Authorization: `Bearer ${authToken}` }
              }
            );

            return {
              battleNumber,
              data: attackStatusResponse.data || { completed: [], pending: [], partialAttack: [] }
            };
          }
          return {
            battleNumber,
            data: { completed: [], pending: [], partialAttack: [] }
          };
        } catch (error) {
          console.error(`${battleNumber}戦目の敵の攻撃状況取得に失敗:`, error);
          return {
            battleNumber,
            data: { completed: [], pending: [], partialAttack: [] }
          };
        }
      });

      const enemyResults = await Promise.all(enemyStatusPromises);

      const newEnemyAttackStatus = { ...enemyAttackStatus };
      enemyResults.forEach(result => {
        newEnemyAttackStatus[result.battleNumber] = result.data;
      });

      setEnemyAttackStatus(newEnemyAttackStatus);

      console.log('全ての戦闘の攻撃状況取得完了');
    } catch (error) {
      console.error('攻撃状況の自動取得に失敗:', error);
    }
  };

  // 認証状態が変更されたときにデータを取得
  useEffect(() => {
    if (isAuthenticated && authToken) {
      // 初回ロード時に選択された戦闘の指示データを取得
      fetchInitialData();
      // 全ての戦闘の攻撃状況を自動取得
      fetchAllBattleStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authToken]);

  // 戦闘番号が変更されたときに指示データを取得
  useEffect(() => {
    if (isAuthenticated && authToken) {
      fetchInitialData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBattle]);

  // 自動保存の処理を更新
  const autoSave = useCallback(
    debounce(async () => {
      if (!isAuthenticated) return;

      try {
        await saveInstructions({
          instructions: towerStatus,
          enemyRatings: userRatings,
          battleNumber: selectedBattle
        });
      } catch (error) {
        console.error('自動保存に失敗しました:', error);
      }
    }, 0),
    [isAuthenticated, towerStatus, userRatings, selectedBattle]
  );

  // dirtyなときだけ自動保存
  useEffect(() => {
    if (isAuthenticated && isDirty) {
      autoSave();
    }
  }, [towerStatus, userRatings, isDirty, isAuthenticated]);

  // ログアウト処理
  const handleLogout = () => {
    localStorage.removeItem('authTokenRaidSkill');
    setAuthToken('');
    setIsAuthenticated(false);
  };

  // 個別メンバーの完了状態を切り替え
  const handleToggleMemberCompletion = async (towerId, enemyIndex) => {
    try {
      const completedMembers = towerStatus[towerId]?.completedMembers || [];
      const isCompleted = completedMembers.includes(enemyIndex);

      if (isCompleted) {
        // 完了を取り消し
        const response = await axios.post(
          `${API_BASE_URL}/api/guild-war-instructions-nihon/uncomplete-member`,
          {
            battleNumber: selectedBattle,
            towerNumber: towerId,
            enemyIndex: enemyIndex
          },
          {
            headers: { Authorization: `Bearer ${authToken}` }
          }
        );

        // ローカルの状態を更新
        setTowerStatus(prev => ({
          ...prev,
          [towerId]: {
            ...prev[towerId],
            completedMembers: completedMembers.filter(idx => idx !== enemyIndex)
          }
        }));

        if (response.data.version) {
          setInstructionsVersion(response.data.version);
        }
      } else {
        // 完了にする
        const response = await axios.post(
          `${API_BASE_URL}/api/guild-war-instructions-nihon/complete-member`,
          {
            battleNumber: selectedBattle,
            towerNumber: towerId,
            enemyIndex: enemyIndex
          },
          {
            headers: { Authorization: `Bearer ${authToken}` }
          }
        );

        // ローカルの状態を更新
        setTowerStatus(prev => ({
          ...prev,
          [towerId]: {
            ...prev[towerId],
            completedMembers: [...completedMembers, enemyIndex]
          }
        }));

        if (response.data.version) {
          setInstructionsVersion(response.data.version);
        }
      }
    } catch (error) {
      console.error('メンバー完了状態の更新に失敗しました:', error);
      alert('メンバー完了状態の更新に失敗しました');
    }
  };

  // タブナビゲーション
  const TabNavigation = () => {
    return (
      <>
        {/* メインタブ */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'instructionManagement' ? 'active' : ''}`}
            onClick={() => setActiveTab('instructionManagement')}
          >
            指示管理
          </button>
          <button
            className={`tab-button ${activeTab === 'battleStatus' ? 'active' : ''}`}
            onClick={() => setActiveTab('battleStatus')}
          >
            対戦状況
          </button>
        </div>

        {/* サブタブ */}
        {activeTab === 'instructionManagement' && (
          <div className="subtab-navigation">
            <button
              className={`subtab-button ${instructionSubTab === 'instructions' ? 'active' : ''}`}
              onClick={() => setInstructionSubTab('instructions')}
            >
              指示
            </button>
            <button
              className={`subtab-button ${instructionSubTab === 'enemy' ? 'active' : ''}`}
              onClick={() => setInstructionSubTab('enemy')}
            >
              敵防衛
            </button>
          </div>
        )}

        {activeTab === 'battleStatus' && (
          <div className="subtab-navigation">
            <button
              className={`subtab-button ${battleStatusSubTab === 'battleMap' ? 'active' : ''}`}
              onClick={() => setBattleStatusSubTab('battleMap')}
            >
              全体
            </button>
            <button
              className={`subtab-button ${battleStatusSubTab === 'attackStatus' ? 'active' : ''}`}
              onClick={() => setBattleStatusSubTab('attackStatus')}
            >
              個別
            </button>
          </div>
        )}

        <style jsx>{`
          .subtab-navigation {
            display: flex;
            justify-content: center;
            margin-bottom: 16px;
            background: #f1f1f1;
            border-radius: 8px;
            padding: 8px;
          }
          
          .subtab-button {
            padding: 8px 16px;
            margin: 0 8px;
            border: none;
            background: transparent;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.2s, color 0.2s;
          }
          
          .subtab-button:hover {
            background: #e0e0e0;
          }
          
          .subtab-button.active {
            background: #4a90e2;
            color: white;
          }
        `}</style>
      </>
    );
  };

  // 指示情報を削除する関数
  const deleteInstructions = async () => {
    const confirmDelete = window.confirm(`${selectedBattle}戦目の指示情報を削除しますか？この操作は取り消せません。`);
    if (!confirmDelete) return;

    try {
      setIsLoading(true);
      await axios.delete(
        `${API_BASE_URL}/api/guild-war-instructions-nihon/delete-instructions/${selectedBattle}`,
        {
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      // 削除成功後、データをリセット
      setTowerStatus({});
      setEnemyTowers({});
      setUserRatings({});
      setInstructionsVersion(1);
      setIsDirty(false);

      setBattleUpdateMessage(`✅ ${selectedBattle}戦目の指示情報を削除しました`);
      setTimeout(() => setBattleUpdateMessage(''), 3000);
    } catch (error) {
      console.error('指示情報の削除に失敗しました:', error);
      setBattleUpdateMessage(`❌ 削除に失敗しました`);
      setTimeout(() => setBattleUpdateMessage(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  // 戦闘選択UIコンポーネント
  const BattleSelector = () => {
    return (
      <div className="battle-selector">
        <div className="battle-buttons">
          {[1, 2, 3].map(battleNum => (
            <button
              key={battleNum}
              className={`battle-button ${selectedBattle === battleNum ? 'active' : ''}`}
              onClick={() => setSelectedBattle(battleNum)}
            >
              {battleNum}戦目
            </button>
          ))}
        </div>
        {battleUpdateMessage && (
          <p className={`battle-update-message ${battleUpdateMessage.includes('✅') ? 'success' : 'error'}`}>
            {battleUpdateMessage}
          </p>
        )}
      </div>
    );
  };

  // 削除セクションコンポーネント
  const DeleteSection = () => {
    return (
      <div className="delete-section">
        <button
          onClick={deleteInstructions}
          className="delete-button"
          disabled={isLoading}
        >
          指示削除
        </button>
      </div>
    );
  };

  // 指示タブの内容を更新
  const InstructionsTab = () => {
    const handleTowerGroupAssignment = (towerId, group) => {
      // 「防衛配置外&時間ない人」を選択時、バフがしきい値以上ならアラート
      if (group === '防衛配置外&時間ない人') {
        const tower = towerStatus[towerId];
        const towerBuff = tower?.buffLevel || 0;
        const maxAttackerBuff = tower?.attackers?.reduce((max, a) => Math.max(max, a?.buffLevel || 0), 0) || 0;
        const actualBuff = Math.max(towerBuff, maxAttackerBuff);
        if (actualBuff >= BUFF_ALERT_THRESHOLD) {
          window.alert(`⚠️ 防衛配置外で${actualBuff}バフまで攻撃したあとに「防衛配置外&時間ない人」で攻撃する設定です。\n\n攻撃者の欄は、バフ完了後の攻撃者を選択してください。`);
        }
      }
      setTowerStatus(prev => ({
        ...prev,
        [towerId]: {
          ...prev[towerId],
          towerGroup: group,
          attackers: prev[towerId]?.attackers || []
        }
      }));
      setIsDirty(true);
    };

    const handleTowerOrderAssignment = (towerId, order) => {
      setTowerStatus(prev => ({
        ...prev,
        [towerId]: {
          ...prev[towerId],
          towerOrder: order ? parseInt(order) : '',
          attackers: prev[towerId]?.attackers || []
        }
      }));
      setIsDirty(true);
    };

    // バフレベルの設定を処理する関数を追加
    const handleBuffLevelAssignment = (towerId, buffLevel) => {
      const parsedBuff = buffLevel ? parseInt(buffLevel) : null;
      // バフがしきい値以上で「防衛配置外&時間ない人」が設定されている場合アラート
      if (parsedBuff >= BUFF_ALERT_THRESHOLD) {
        const tower = towerStatus[towerId];
        const hasTowerGroup = tower?.towerGroup === '防衛配置外&時間ない人';
        const hasAttackerGroup = tower?.attackers?.some(a => a && a.group === '防衛配置外&時間ない人');
        if (hasTowerGroup || hasAttackerGroup) {
          window.alert(`⚠️ 防衛配置外で${parsedBuff}バフまで攻撃したあとに「防衛配置外&時間ない人」で攻撃する設定です。\n\n攻撃者の欄は、バフ完了後の攻撃者を選択してください。`);
        }
      }
      setTowerStatus(prev => ({
        ...prev,
        [towerId]: {
          ...prev[towerId],
          buffLevel: parsedBuff,
          attackers: prev[towerId]?.attackers || []
        }
      }));
      setIsDirty(true);
    };

    // 個別指示でバフが設定されているかチェックする関数を追加
    const hasIndividualBuffs = (towerId) => {
      const tower = towerStatus[towerId];
      if (!tower || !tower.attackers) return false;
      return tower.attackers.some(attacker => attacker && attacker.buffLevel >= 2);
    };

    const handleAttackerAssignment = (towerId, index, group) => {
      if (towerStatus[towerId]?.towerGroup) return; // 塔全体のグループが設定されている場合は個別設定不可

      // 「防衛配置外&時間ない人」を選択時、その攻撃者または塔のバフがしきい値以上ならアラート
      if (group === '防衛配置外&時間ない人') {
        const tower = towerStatus[towerId];
        const towerBuff = tower?.buffLevel || 0;
        const attackerBuff = tower?.attackers?.[index]?.buffLevel || 0;
        const actualBuff = Math.max(towerBuff, attackerBuff);
        if (actualBuff >= BUFF_ALERT_THRESHOLD) {
          window.alert(`⚠️ 防衛配置外で${actualBuff}バフまで攻撃したあとに「防衛配置外&時間ない人」で攻撃する設定です。\n\n攻撃者の欄は、バフ完了後の攻撃者を選択してください。`);
        }
      }

      setTowerStatus(prev => {
        const newAttackers = [...(prev[towerId]?.attackers || [])];
        newAttackers[index] = {
          ...newAttackers[index],
          group
        };
        return {
          ...prev,
          [towerId]: {
            ...prev[towerId],
            attackers: newAttackers
          }
        };
      });
      setIsDirty(true);
    };

    const handleOrderChange = (towerId, index, order) => {
      if (towerStatus[towerId]?.towerOrder) return;

      setTowerStatus(prev => {
        const newAttackers = [...(prev[towerId]?.attackers || [])];
        newAttackers[index] = {
          ...newAttackers[index],
          order: order ? parseInt(order) : ''
        };
        return {
          ...prev,
          [towerId]: {
            ...prev[towerId],
            attackers: newAttackers
          }
        };
      });
      setIsDirty(true);
    };

    // バフレベルの設定を処理する関数を追加（個別指示用）
    const handleAttackerBuffLevel = (towerId, index, buffLevel) => {
      if (towerStatus[towerId]?.buffLevel) return;

      const parsedBuff = buffLevel ? parseInt(buffLevel) : null;
      // バフがしきい値以上で「防衛配置外&時間ない人」が設定されている場合アラート
      if (parsedBuff >= BUFF_ALERT_THRESHOLD) {
        const tower = towerStatus[towerId];
        const hasAttackerGroup = tower?.attackers?.[index]?.group === '防衛配置外&時間ない人';
        if (hasAttackerGroup) {
          window.alert(`⚠️ 防衛配置外で${parsedBuff}バフまで攻撃したあとに「防衛配置外&時間ない人」で攻撃する設定です。\n\n攻撃者の欄は、バフ完了後の攻撃者を選択してください。`);
        }
      }

      setTowerStatus(prev => {
        const newAttackers = [...(prev[towerId]?.attackers || [])];
        newAttackers[index] = {
          ...newAttackers[index],
          buffLevel: parsedBuff
        };
        return {
          ...prev,
          [towerId]: {
            ...prev[towerId],
            attackers: newAttackers
          }
        };
      });
      setIsDirty(true);
    };

    const handleTowerCompletion = (towerId, isCompleted) => {
      setTowerStatus(prev => ({
        ...prev,
        [towerId]: {
          ...prev[towerId],
          completed: isCompleted,
          attackers: prev[towerId]?.attackers || []
        }
      }));
      setIsDirty(true);
    };

    const toggleIndividualSettings = (towerId) => {
      setShowIndividualSettings(prev => ({
        ...prev,
        [towerId]: !prev[towerId]
      }));
    };

    const ratingOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const orderOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    // 塔ごとの順番オプションを取得する関数
    const getOrderOptions = (towerId) => {
      const towerNum = parseInt(towerId);
      if (towerNum >= 10) {
        // 10塔は3以上
        return [3, 4, 5, 6, 7, 8, 9, 10];
      } else if (towerNum >= 7) {
        // 7,8,9塔は2以上
        return [2, 3, 4, 5, 6, 7, 8, 9, 10];
      } else {
        // 1-6塔は1以上
        return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
      }
    };

    const getAllTowers = () => {
      return Object.values(towerStructure).reduce((acc, floor) => {
        return [...acc, ...floor.towers];
      }, []);
    };

    const getSortedTowers = (floor) => {
      if (!sortByOrder) {
        return floor.towers;
      }
      return [...floor.towers].sort((a, b) => {
        const orderA = towerStatus[a]?.towerOrder || 999;
        const orderB = towerStatus[b]?.towerOrder || 999;
        return orderA - orderB;
      });
    };

    const renderTowerCard = (towerId) => {
      const floor = parseInt(towerId) <= 6 ? 1 : parseInt(towerId) <= 9 ? 2 : 3;
      // 塔の防衛者に1人でもメイン塔（remainDefenceCount === 5）がいるかチェック
      // remainDefenceCountが数値でも文字列でも正しく判定するためparseIntを使用
      const isMainTower = enemyTowers[towerId]?.some(member =>
        typeof member === 'object' && parseInt(member?.remainDefenceCount, 10) === 5
      );

      return (
        <div
          key={towerId}
          className={`tower-card ${towerStatus[towerId]?.completed ? 'completed' : ''}`}
          data-floor={floor}
        >
          <h4>
            {towerId}塔
            {isMainTower && <span className="main-tower-badge">メイン</span>}
          </h4>
          {enemyTowers[towerId] ? (
            <>
              <div className="tower-controls">
                <h5>塔全体の指定</h5>
                <div className="tower-group-controls">
                  <select
                    value={towerStatus[towerId]?.towerGroup || ''}
                    onChange={(e) => handleTowerGroupAssignment(towerId, e.target.value)}
                    className="group-select"
                    disabled={towerStatus[towerId]?.completed}
                  >
                    <option value="">攻撃者</option>
                    {groups.map(group => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                  <select
                    value={towerStatus[towerId]?.towerOrder || ''}
                    onChange={(e) => handleTowerOrderAssignment(towerId, e.target.value)}
                    className="order-select"
                    disabled={towerStatus[towerId]?.completed}
                  >
                    <option value="">順番</option>
                    {getOrderOptions(towerId).map(order => (
                      <option key={order} value={order}>{order}</option>
                    ))}
                  </select>
                  <select
                    value={towerStatus[towerId]?.buffLevel || ''}
                    onChange={(e) => handleBuffLevelAssignment(towerId, e.target.value)}
                    className="buff-select"
                    disabled={towerStatus[towerId]?.completed || hasIndividualBuffs(towerId)}
                  >
                    <option value="">バフ</option>
                    {buffLevels.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                className="toggle-individual-settings"
                onClick={() => toggleIndividualSettings(towerId)}
              >
                個別指定 {showIndividualSettings[towerId] ? '▼' : '▶'}
              </button>
              {showIndividualSettings[towerId] && (
                <div className="attackers-list">
                  {enemyTowers[towerId].map((member, index) => {
                    const memberName = typeof member === 'string' ? member : member?.name || '';
                    const isCompleted = towerStatus[towerId]?.completedMembers?.includes(index) || false;
                    return (
                      <div key={index} className={`attacker-slot ${isCompleted ? 'completed' : ''}`}>
                        <div className="enemy-info">
                          <p className="enemy-name">
                            {memberName}
                            {isCompleted && <span className="completed-badge">✓完了</span>}
                          </p>
                          <StarRating
                            towerId={towerId}
                            userIndex={index}
                            currentRating={userRatings[memberName]}
                            onRatingChange={(newRating) => handleUserRating(memberName, newRating)}
                          />
                        </div>
                        <div className="attacker-controls">
                          <select
                            value={towerStatus[towerId]?.attackers?.[index]?.group || ''}
                            onChange={(e) => handleAttackerAssignment(towerId, index, e.target.value)}
                            className="group-select"
                            disabled={towerStatus[towerId]?.completed || towerStatus[towerId]?.towerGroup || isCompleted}
                          >
                            <option value="">攻撃者</option>
                            {groups.map(group => (
                              <option key={group} value={group}>{group}</option>
                            ))}
                          </select>
                          <select
                            value={towerStatus[towerId]?.attackers?.[index]?.order || ''}
                            onChange={(e) => handleOrderChange(towerId, index, e.target.value)}
                            className="order-select"
                            disabled={towerStatus[towerId]?.completed || towerStatus[towerId]?.towerOrder || isCompleted}
                          >
                            <option value="">順番</option>
                            {getOrderOptions(towerId).map(order => (
                              <option key={order} value={order}>{order}</option>
                            ))}
                          </select>
                          <select
                            value={towerStatus[towerId]?.attackers?.[index]?.buffLevel || ''}
                            onChange={(e) => handleAttackerBuffLevel(towerId, index, e.target.value)}
                            className="buff-select"
                            disabled={towerStatus[towerId]?.completed || towerStatus[towerId]?.buffLevel || isCompleted}
                          >
                            <option value="">バフ</option>
                            {buffLevels.map(level => (
                              <option key={level} value={level}>{level}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleToggleMemberCompletion(towerId, index)}
                            className={`complete-button ${isCompleted ? 'completed' : ''}`}
                          >
                            {isCompleted ? '✓ 完了' : '完了にする'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="tower-controls">
                <h5>塔全体の指定</h5>
                <div className="tower-group-controls">
                  <select
                    value={towerStatus[towerId]?.towerGroup || ''}
                    onChange={(e) => handleTowerGroupAssignment(towerId, e.target.value)}
                    className="group-select"
                    disabled={towerStatus[towerId]?.completed}
                  >
                    <option value="">攻撃者</option>
                    {groups.map(group => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                  <select
                    value={towerStatus[towerId]?.towerOrder || ''}
                    onChange={(e) => handleTowerOrderAssignment(towerId, e.target.value)}
                    className="order-select"
                    disabled={towerStatus[towerId]?.completed}
                  >
                    <option value="">-</option>
                    {getOrderOptions(towerId).map(order => (
                      <option key={order} value={order}>{order}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="attackers-list">
                <h5>個別指定</h5>
                <p className="enemy-name">未取得</p>
              </div>
            </>
          )}
          <div className="tower-status">
            <label>
              <input
                type="checkbox"
                checked={towerStatus[towerId]?.completed || false}
                onChange={(e) => handleTowerCompletion(towerId, e.target.checked)}
              />
              攻撃完了
            </label>
          </div>
        </div>
      );
    };

    return (
      <div className="instructions-tab">
        <div className="controls">
          <button
            onClick={() => setSortByOrder(!sortByOrder)}
            className={`sort-button ${sortByOrder ? 'active' : ''}`}
          >
            {sortByOrder ? '通常表示' : '順番表示'}
          </button>
        </div>
        {sortByOrder ? (
          <div className="towers-grid">
            {getAllTowers()
              .sort((a, b) => {
                const orderA = towerStatus[a]?.towerOrder || 999;
                const orderB = towerStatus[b]?.towerOrder || 999;
                return orderA - orderB;
              })
              .map(renderTowerCard)}
          </div>
        ) : (
          Object.entries(towerStructure).map(([floorKey, floor]) => {
            const floorNumber = floorKey === 'floor1' ? 1 : floorKey === 'floor2' ? 2 : 3;
            return (
              <div key={floorKey} className="floor-section" data-floor={floorNumber}>
                <h3 className="floor-title">{floorKey === 'floor1' ? '1層' : floorKey === 'floor2' ? '2層' : '3層'}</h3>
                <div className="towers-grid">
                  {getSortedTowers(floor).map(renderTowerCard)}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  // 敵タブの内容を更新
  const EnemyTab = () => {
    const [updateMessage, setUpdateMessage] = useState('');

    // 敵の防衛情報タブ
    const EnemyDefenseTab = () => {
      const [manualMode, setManualMode] = useState(true);
      const [editingCell, setEditingCell] = useState(null);
      const [tempInput, setTempInput] = useState({});
      const [selectedDefender, setSelectedDefender] = useState(null);
      const [loadingDetail, setLoadingDetail] = useState(false);

      const handleDetailClick = async (towerId, member) => {
        if (member && typeof member === 'object' && member.deckInfoJson) {
          setSelectedDefender({ member, towerLabel: towerId });
          return;
        }

        const memberName = typeof member === 'object' && member !== null ? member.name : member;
        if (!memberName) return;

        setLoadingDetail(true);
        try {
          const response = await axios.get(
            `${API_BASE_URL}/api/guild-war-instructions-nihon/tower-detail?battleNumber=${selectedBattle}&towerId=${towerId}`,
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
          if (response.data?.users) {
            const defender = response.data.users.find(m => m?.name === memberName);
            if (defender) {
              setSelectedDefender({ member: defender, towerLabel: towerId });
            }
          }
        } catch (error) {
          console.error('Failed to fetch defender details:', error);
        } finally {
          setLoadingDetail(false);
        }
      };

      // タワーごとのメンバー数の制限を定義
      const getTowerMemberLimit = (towerId) => {
        if (towerId >= 1 && towerId <= 6) return 2;
        if (towerId >= 7 && towerId <= 9) return 3;
        return 4; // タワー10の場合
      };

      // 初期タワー構造を生成
      const initializeTowers = () => {
        const towers = {};
        for (let i = 1; i <= 10; i++) {
          towers[i] = Array(getTowerMemberLimit(i)).fill('');
        }
        return towers;
      };

      // タワーデータが空の場合は初期構造を使用
      useEffect(() => {
        if (!enemyTowers || Object.keys(enemyTowers).length === 0) {
          setEnemyTowers(initializeTowers());
        }
      }, []);

      const handleRefresh = async () => {
        try {
          setUpdateMessage('データを更新中...');
          const response = await axios.get(
            `${API_BASE_URL}/api/guild-war-instructions-nihon/all-towers?battleNumber=${selectedBattle}`,
            {
              headers: { Authorization: `Bearer ${authToken}` }
            }
          );

          if (response.data && response.data.towers) {
            const formattedTowers = {};
            Object.entries(response.data.towers).forEach(([towerId, fetchMembers]) => {
              const prevMembers = enemyTowers[towerId] || [];
              const mergedMembers = (Array.isArray(fetchMembers) ? fetchMembers : []).map((fetchMember, i) => {
                const prevMember = prevMembers[i];
                const prevIsMain = typeof prevMember === 'object' && prevMember !== null ? prevMember.isMain : false;
                const prevCount = typeof prevMember === 'object' && prevMember !== null
                  ? parseInt(prevMember.remainDefenceCount, 10)
                  : 0;

                // 既存のメイン塔判定を引き継ぐ
                const isMain = prevIsMain || prevCount === 5;

                if (typeof fetchMember === 'string') {
                  return { name: fetchMember, isMain, remainDefenceCount: 0 };
                }
                return {
                  ...fetchMember,
                  isMain: isMain || parseInt(fetchMember?.remainDefenceCount, 10) === 5
                };
              });
              formattedTowers[towerId] = mergedMembers;
            });
            setEnemyTowers(formattedTowers);

            const newRatings = { ...userRatings };
            Object.values(formattedTowers).forEach(members => {
              if (Array.isArray(members)) {
                members.forEach(member => {
                  // 文字列とオブジェクトの両方に対応
                  const memberName = typeof member === 'string' ? member : member?.name;
                  if (memberName && memberName.trim() !== '' && !newRatings[memberName]) {
                    const score = typeof member === 'object' && member !== null && member.strengthScore != null
                      ? member.strengthScore
                      : null;
                    const scoreToStars = (s) => {
                      const r = Math.round(s);
                      if (r >= 75) return 5;
                      if (r >= 50) return 4;
                      if (r >= 30) return 3;
                      if (r >= 10) return 2;
                      return 1;
                    };
                    newRatings[memberName] = score != null ? scoreToStars(score) : 0;
                  }
                });
              }
            });
            setUserRatings(newRatings);

            // 即座にRedisに保存（strengthScore含む）
            saveEnemyTowers({
              towers: formattedTowers,
              userRatings: newRatings,
              battleNumber: selectedBattle
            }).then(() => setIsDirty(false));

            // 敵ギルドのgidが取得できていれば、敵の攻撃状況も更新
            if (response.data.gid) {
              fetchEnemyAttackStatus(response.data.gid);
            }

            setUpdateMessage('✅ 更新完了');
            setTimeout(() => setUpdateMessage(''), 3000);
          }
        } catch (error) {
          console.error('Error fetching enemy towers:', error);
          setUpdateMessage('❌ 更新失敗');
          setTimeout(() => setUpdateMessage(''), 3000);
        }
      };

      const handleNameEdit = (towerId, index, newName) => {
        const inputKey = `${towerId}-${index}`;
        setTempInput(prev => ({
          ...prev,
          [inputKey]: typeof newName === 'string' ? newName : ''
        }));
      };

      const handleInputComplete = async (towerId, index) => {
        const inputKey = `${towerId}-${index}`;
        const newName = tempInput[inputKey] || '';

        const updatedTowers = { ...enemyTowers };
        if (!updatedTowers[towerId] || !Array.isArray(updatedTowers[towerId])) {
          updatedTowers[towerId] = Array(getTowerMemberLimit(parseInt(towerId))).fill('');
        }
        const newTowers = [...updatedTowers[towerId]];
        const existingMember = newTowers[index];
        const isMain = typeof existingMember === 'object' && existingMember !== null ? existingMember.isMain : false;
        const count = typeof existingMember === 'object' && existingMember !== null ? existingMember.remainDefenceCount : 0;

        if (typeof newName === 'string') {
          newTowers[index] = { name: newName.trim(), isMain, remainDefenceCount: count };
        } else {
          newTowers[index] = newName;
        }
        updatedTowers[towerId] = newTowers;

        setEnemyTowers(updatedTowers);

        try {
          // 名前の変更を保存
          await saveEnemyTowers({
            towers: updatedTowers,
            userRatings,
            battleNumber: selectedBattle
          });
        } catch (error) {
          console.error('敵タワー情報の保存に失敗しました:', error);
          alert('敵タワー情報の保存に失敗しました。もう一度お試しください。');
        }

        setEditingCell(null);
        setTempInput(prev => {
          const updated = { ...prev };
          delete updated[inputKey];
          return updated;
        });
      };

      const handleToggleMainTower = async (towerId) => {
        const updatedTowers = { ...enemyTowers };
        const towerMembers = updatedTowers[towerId] || [];

        const currentIsMain = towerMembers.some(m => typeof m === 'object' && m !== null && (m.isMain || parseInt(m.remainDefenceCount, 10) === 5));

        const newMembers = [...towerMembers];
        if (newMembers.length > 0) {
          const firstMember = newMembers[0];
          if (typeof firstMember === 'string') {
            newMembers[0] = { name: firstMember, isMain: !currentIsMain, remainDefenceCount: 0 };
          } else if (firstMember) {
            newMembers[0] = { ...firstMember, isMain: !currentIsMain };
          } else {
            newMembers[0] = { name: '', isMain: !currentIsMain, remainDefenceCount: 0 };
          }
        }

        updatedTowers[towerId] = newMembers;
        setEnemyTowers(updatedTowers);

        try {
          await saveEnemyTowers({
            towers: updatedTowers,
            userRatings,
            battleNumber: selectedBattle
          });
        } catch (error) {
          console.error('メイン塔設定の保存に失敗しました:', error);
          alert('メイン塔設定の保存に失敗しました。もう一度お試しください。');
        }
      };

      return (
        <div className="enemy-defense-tab">
          <div className="controls">
            <button onClick={handleRefresh} className="refresh-button">
              情報更新
            </button>
            {updateMessage && (
              <p className={`update-message ${updateMessage.includes('✅') ? 'success' : 'error'}`}>
                {updateMessage}
              </p>
            )}
          </div>
          {Object.entries(enemyTowers).length > 0 ? (
            Object.entries(enemyTowers).map(([towerId, tower]) => {
              const floor = parseInt(towerId) <= 6 ? 1 : parseInt(towerId) <= 9 ? 2 : 3;
              const isMainTower = tower?.some(member =>
                typeof member === 'object' && member !== null &&
                (parseInt(member?.remainDefenceCount, 10) === 5 || member?.isMain === true)
              );

              return (
                <div key={towerId} className="tower-section" data-floor={floor}>
                  <h3 className="tower-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {towerId}塔
                    {isMainTower && <span className="main-tower-badge">メイン</span>}
                    <label style={{ fontSize: '13px', fontWeight: 'normal', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginLeft: 'auto', color: '#555' }}>
                      <input
                        type="checkbox"
                        checked={!!isMainTower}
                        onChange={() => handleToggleMainTower(towerId)}
                      />
                      メイン塔に設定
                    </label>
                  </h3>
                  <div className="tower-controls">
                    {Array.from({ length: getTowerMemberLimit(parseInt(towerId)) }).map((_, index) => {
                      const inputKey = `${towerId}-${index}`;
                      const isEditing = editingCell === inputKey;
                      const member = tower?.[index];
                      const memberName = typeof member === 'string' ? member : member?.name || '';
                      const currentValue = isEditing ? (tempInput[inputKey] ?? memberName ?? '') : (memberName || '');

                      return (
                        <div key={index} className="enemy-slot">
                          <div className="enemy-info">
                            {isEditing ? (
                              <input
                                type="text"
                                value={currentValue}
                                onChange={(e) => handleNameEdit(towerId, index, e.target.value)}
                                onBlur={() => handleInputComplete(towerId, index)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleInputComplete(towerId, index);
                                  }
                                }}
                                className="enemy-name-input"
                                autoFocus
                              />
                            ) : (
                              <div
                                className="enemy-name-placeholder"
                                onClick={() => {
                                  setEditingCell(inputKey);
                                  setTempInput(prev => ({
                                    ...prev,
                                    [inputKey]: memberName || ''
                                  }));
                                }}
                              >
                                {currentValue || '未取得'}
                              </div>
                            )}
                          </div>
                          <div className="enemy-controls">
                            {currentValue && currentValue.trim() !== '' && (
                              <>
                                <StarRating
                                  towerId={towerId}
                                  userIndex={index}
                                  currentRating={userRatings[currentValue]}
                                  onRatingChange={(newRating) => handleUserRating(currentValue, newRating)}
                                />
                                {member && typeof member === 'object' && member.strengthScore != null && (
                                  (() => {
                                    const sc = Math.round(member.strengthScore);
                                    const cls = sc >= 75 ? 'strength-danger' : sc >= 50 ? 'strength-strong' : sc >= 30 ? 'strength-average' : 'strength-weak';
                                    return <span className={`strength-tag ${cls}`}>{sc}</span>;
                                  })()
                                )}
                                {currentValue && currentValue.trim() !== '' && (
                                  <button
                                    className="detail-button"
                                    onClick={() => handleDetailClick(towerId, member)}
                                    disabled={loadingDetail}
                                    title="詳細を表示"
                                  >
                                    {loadingDetail ? '⏳' : '📊'}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <div>データを読み込み中...</div>
          )}
          {loadingDetail && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.5)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 9999
            }}>
              <div style={{
                background: '#fff', padding: '24px 40px', borderRadius: '12px',
                fontSize: '16px', fontWeight: 600, color: '#333'
              }}>
                詳細データを読み込んでいます...
              </div>
            </div>
          )}
          {selectedDefender && (
            <DefenderDetailModal
              defender={selectedDefender.member}
              towerLabel={selectedDefender.towerLabel}
              authToken={authToken}
              onClose={() => setSelectedDefender(null)}
            />
          )}
        </div>
      );
    };

    return (
      <div className="enemy-tab">
        <EnemyDefenseTab />
        <style jsx>{`
          .subtab-navigation {
            display: flex;
            justify-content: center;
            margin-bottom: 16px;
            background: #f1f1f1;
            border-radius: 8px;
            padding: 8px;
          }
          
          .subtab-button {
            padding: 8px 16px;
            margin: 0 8px;
            border: none;
            background: transparent;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.2s, color 0.2s;
          }
          
          .subtab-button:hover {
            background: #e0e0e0;
          }
          
          .subtab-button.active {
            background: #4a90e2;
            color: white;
          }
          
          .enemy-name-placeholder {
            padding: 8px 12px;
            background-color: #f5f5f5;
            border-radius: 4px;
            cursor: pointer;
            transition: background-color 0.2s;
            color: #666;
          }
          
          .enemy-name-placeholder:hover {
            background-color: #e0e0e0;
          }
          
          .enemy-name-input {
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            width: 100%;
            font-size: 14px;
            transition: border-color 0.2s;
            background-color: #f5f5f5;
          }
          
          .enemy-name-input:focus {
            border-color: #4a90e2;
            outline: none;
            box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
            background-color: #fff;
          }

          .enemy-name-input:hover:not(:focus) {
            background-color: #e8e8e8;
          }
          
          .member-status {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 4px;
          }

          .member-info {
            display: flex;
            align-items: center;
            gap: 12px;
            width: 100%;
          }

          .member-name {
            font-weight: 500;
            min-width: 80px;
          }

          .win-rate-container {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
          }

          .win-rate-bar {
            width: 60px;
            height: 8px;
            background: #e0e0e0;
            border-radius: 4px;
            overflow: hidden;
          }

          .win-rate-stacked {
            display: flex;
            height: 100%;
          }

          .win-rate-segment {
            height: 100%;
            transition: width 0.3s ease;
          }

          .win-rate-segment.win {
            background-color: #4caf50;
          }

          .win-rate-segment.lose {
            background-color: #f44336;
          }

          .win-rate-text {
            font-size: 0.85em;
            font-weight: 600;
            color: #555;
            min-width: 50px;
          }

          .status-list li {
            list-style: none;
          }

          .status-column {
            background: white;
            padding: 16px;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }

          .status-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: pointer;
            padding: 8px;
            border-radius: 4px;
            transition: background-color 0.2s;
          }

          .status-header:hover {
            background-color: rgba(0, 0, 0, 0.05);
          }

          .toggle-icon {
            font-size: 1.2em;
            color: #666;
          }

          .status-list {
            margin-top: 12px;
            padding: 0;
          }
        `}</style>
      </div>
    );
  };

  // 対戦状況データを取得する関数
  const fetchBattleStatusData = async (opponentType, battleNumber) => {
    try {
      // 1. メイン塔情報を取得
      const enemyTowersResponse = await axios.get(
        `${API_BASE_URL}/api/guild-war-instructions-nihon/enemy-towers`,
        {
          params: { battleNumber },
          headers: { Authorization: `Bearer ${authToken}` }
        }
      );

      const enemyTowersData = enemyTowersResponse.data.towers || {};
      const mainTowerMap = {};

      // メイン塔の判定
      Object.keys(enemyTowersData).forEach(towerNum => {
        const defenders = enemyTowersData[towerNum] || [];
        const defendersArray = Array.isArray(defenders) ? defenders : [];
        const isMainTower = defendersArray.some(def => {
          if (typeof def === 'string') return false;
          // remainDefenceCountが数値でも文字列でも正しく判定するためparseIntを使用
          return def && parseInt(def.remainDefenceCount, 10) === 5;
        });
        mainTowerMap[towerNum] = isMainTower;
      });

      // 2. 実際のデータを取得（GuildWarNihon.jsと同じAPI）
      const now = new Date();
      const requestDate = now.toISOString().split('T')[0];

      // 10塔分のデータを並列で取得
      const towerPromises = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(towerNum =>
        axios.get(
          `${API_BASE_URL}/api/guild-war-nihon`,
          {
            params: {
              date: requestDate,
              opponent: opponentType,
              match: battleNumber,
              tower: towerNum
            },
            headers: { Authorization: `Bearer ${authToken}` }
          }
        ).catch(error => {
          console.error(`Tower ${towerNum} fetch error:`, error);
          return null;
        })
      );

      const responses = await Promise.all(towerPromises);

      const towersData = {};

      responses.forEach((response, index) => {
        if (response && response.data) {
          const towerNum = index + 1;
          const defenders = response.data.result?.guildWarDefenceDeckResponses || [];

          // メイン塔の判定
          // 1. mainTowerMapから取得を試みる（キーを文字列に変換して参照）
          let isMainTower = mainTowerMap[String(towerNum)] || false;

          // 2. mainTowerMapに情報がない場合、remainDefenceCountから判定
          // remainDefenceCountが数値でも文字列でも正しく判定するためparseIntを使用
          if (!isMainTower) {
            isMainTower = defenders.some(def => parseInt(def.remainDefenceCount, 10) === 5);
          }

          // 残り攻撃回数の計算
          let remainingAttacks = 0;
          defenders.forEach(def => {
            remainingAttacks += (def.remainDefenceCount || 0);
          });

          // 分母の計算
          const maxAttacks = isMainTower ? defenders.length * 5 : defenders.length * 3;

          // 防衛者の情報を保存
          const defendersList = defenders.map(def => ({
            name: def.mergedGuildPlayer?.name || '不明',
            remainDefenceCount: def.remainDefenceCount || 0,
            defenseCount: def.winCount || 0,
            pvpLeaguePhase: def.mergedGuildPlayer?.pvpLeaguePhase || '不明'
          }));

          // ステータスの判定
          const totalWinCount = defendersList.reduce((sum, def) => sum + def.defenseCount, 0);
          const isDestroyed = remainingAttacks === 0; // 残り回数が0（完全に落とされた）
          const isUnderAttack = remainingAttacks < maxAttacks && remainingAttacks > 0; // 攻撃を受けている
          const isDefending = totalWinCount > 0 && !isUnderAttack && !isDestroyed; // 防衛しているが攻撃は受けていない

          towersData[towerNum] = {
            remainingAttacks,
            maxAttacks,
            isMainTower,
            defenders: defendersList,
            defenderCount: defenders.length,
            isDestroyed,
            isUnderAttack,
            isDefending
          };
        }
      });

      return { towers: towersData };
    } catch (error) {
      console.error('対戦状況データの取得に失敗:', error);
      return { towers: {} };
    }
  };

  // 対戦状況を更新する関数
  const updateBattleStatus = async (opponentType) => {
    try {
      setIsLoading(true);
      const data = await fetchBattleStatusData(opponentType, selectedBattle);

      setBattleStatusData(prev => ({
        ...prev,
        [opponentType]: {
          ...prev[opponentType],
          [selectedBattle]: data
        }
      }));
    } catch (error) {
      console.error('対戦状況の更新に失敗:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 対戦状況タブの内容
  const BattleStatusTab = () => {
    const [updateMessage, setUpdateMessage] = useState('');

    const currentData = battleStatusData[battleMapView]?.[selectedBattle] || { towers: {} };
    const hasData = Object.keys(currentData.towers).length > 0;

    const handleRefresh = async () => {
      try {
        setUpdateMessage('データを更新中...');
        await updateBattleStatus(battleMapView);
        setUpdateMessage('✅ 更新完了');
        setTimeout(() => setUpdateMessage(''), 3000);
      } catch (error) {
        setUpdateMessage('❌ 更新失敗');
        setTimeout(() => setUpdateMessage(''), 3000);
      }
    };

    const handleShowData = async () => {
      try {
        setUpdateMessage('データを取得中...');
        await updateBattleStatus(battleMapView);
        setUpdateMessage('✅ 取得完了');
        setTimeout(() => setUpdateMessage(''), 3000);
      } catch (error) {
        setUpdateMessage('❌ 取得失敗');
        setTimeout(() => setUpdateMessage(''), 3000);
      }
    };

    const handleTowerClick = (towerId) => {
      const towerData = currentData.towers[towerId];
      if (towerData && towerData.defenders && towerData.defenders.length > 0) {
        setSelectedTowerModal({ towerId, data: towerData });
      }
    };

    const closeModal = () => {
      setSelectedTowerModal(null);
    };

    const floors = {
      floor1: [1, 2, 3, 4, 5, 6],
      floor2: [7, 8, 9],
      floor3: [10]
    };

    return (
      <div className="battle-status-tab">
        <div className="subtab-navigation-inner">
          <button
            className={`subtab-button ${battleMapView === 'ally' ? 'active' : ''}`}
            onClick={() => setBattleMapView('ally')}
          >
            自陣
          </button>
          <button
            className={`subtab-button ${battleMapView === 'opponent' ? 'active' : ''}`}
            onClick={() => setBattleMapView('opponent')}
          >
            敵陣
          </button>
        </div>

        {!hasData ? (
          <div className="show-data-container">
            <button onClick={handleShowData} className="show-data-button">
              表示する
            </button>
            {updateMessage && (
              <p className={`update-message ${updateMessage.includes('✅') ? 'success' : 'error'}`}>
                {updateMessage}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="controls">
              <button onClick={handleRefresh} className="refresh-button">
                情報更新
              </button>
              {updateMessage && (
                <p className={`update-message ${updateMessage.includes('✅') ? 'success' : 'error'}`}>
                  {updateMessage}
                </p>
              )}
            </div>

            <div className="battle-map">
              {Object.entries(floors).map(([floorKey, towerIds]) => {
                const floorNumber = floorKey === 'floor1' ? 1 : floorKey === 'floor2' ? 2 : 3;
                return (
                  <div key={floorKey} className="floor-section" data-floor={floorNumber}>
                    <h3 className="floor-title">{floorNumber}層</h3>
                    <div className="towers-grid-status">
                      {towerIds.map(towerId => {
                        const towerData = currentData.towers[towerId] || {
                          remainingAttacks: 0,
                          maxAttacks: 0,
                          isMainTower: false,
                          defenders: [],
                          isDestroyed: false,
                          isUnderAttack: false,
                          isDefending: false
                        };

                        // クラス名を決定（優先順位: 破壊 > 攻撃中 > 防衛中）
                        let statusClass = '';
                        if (towerData.isDestroyed) {
                          statusClass = 'destroyed';
                        } else if (towerData.isUnderAttack) {
                          statusClass = 'under-attack';
                        } else if (towerData.isDefending) {
                          statusClass = 'defending';
                        }

                        return (
                          <div
                            key={towerId}
                            className={`tower-status-card ${statusClass} ${towerData.defenders?.length > 0 ? 'clickable' : ''}`}
                            onClick={() => handleTowerClick(towerId)}
                          >
                            <div className="tower-header">
                              <h4>
                                {towerId}塔
                                {towerData.isMainTower && <span className="main-tower-badge">メイン</span>}
                              </h4>
                            </div>
                            <div className="tower-attacks">
                              <span className="attack-count">
                                {towerData.remainingAttacks}/{towerData.maxAttacks}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedTowerModal && (
              <div className="modal-overlay" onClick={closeModal}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>
                      {selectedTowerModal.towerId}塔の防衛者 ({selectedTowerModal.data.defenderCount}人)
                      {selectedTowerModal.data.isMainTower && <span className="main-tower-badge">メイン</span>}
                    </h3>
                    <button className="close-button" onClick={closeModal}>×</button>
                  </div>
                  <div className="defenders-list-modal">
                    {selectedTowerModal.data.defenders && selectedTowerModal.data.defenders.length > 0 ? (
                      selectedTowerModal.data.defenders.map((defender, index) => {
                        // メイン塔は最大5回、通常塔は最大3回
                        const maxDefence = selectedTowerModal.data.isMainTower ? 5 : 3;
                        return (
                          <div key={index} className="defender-item">
                            <div className="defender-info">
                              <span className="defender-name">{defender.name}</span>
                            </div>
                            <div className="defender-stats">
                              <div className="defender-stat-item">
                                <span className="defender-stat-label">WIN :</span>
                                <span className="defender-stat-value win-count">{defender.defenseCount}</span>
                              </div>
                              <div className="defender-stat-item">
                                <span className="defender-stat-value">{defender.remainDefenceCount}/{maxDefence}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="no-defenders">防衛者情報がありません</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <style jsx>{`
          .show-data-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;
            gap: 16px;
          }
          
          .show-data-button {
            padding: 12px 32px;
            font-size: 16px;
            font-weight: 600;
            background: #4a90e2;
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: background 0.2s, transform 0.1s;
          }
          
          .show-data-button:hover {
            background: #357abd;
            transform: translateY(-2px);
          }
          
          .show-data-button:active {
            transform: translateY(0);
          }
          
          .alert-message {
            background: #fff3cd;
            border: 2px solid #ffc107;
            color: #856404;
            padding: 16px 24px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 500;
            text-align: center;
          }

          .subtab-navigation {
            display: flex;
            justify-content: center;
            margin-bottom: 16px;
            background: #f1f1f1;
            border-radius: 8px;
            padding: 8px;
          }
          
          .subtab-button {
            padding: 8px 16px;
            margin: 0 8px;
            border: none;
            background: transparent;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 500;
            transition: background-color 0.2s, color 0.2s;
          }
          
          .subtab-button:hover {
            background: #e0e0e0;
          }
          
          .subtab-button.active {
            background: #4a90e2;
            color: white;
          }
          
          .subtab-navigation-inner {
            display: flex;
            justify-content: center;
            margin-bottom: 16px;
            background: #e8e8e8;
            border-radius: 6px;
            padding: 6px;
          }

          .battle-map {
            margin-top: 20px;
          }

          .towers-grid-status {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 16px;
            margin-top: 16px;
          }

          .tower-status-card {
            background: white;
            border-radius: 8px;
            padding: 16px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            text-align: center;
            transition: transform 0.2s, box-shadow 0.2s;
          }

          .tower-status-card.clickable {
            cursor: pointer;
          }

          .tower-status-card.clickable:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
          }

          .tower-status-card.destroyed {
            border: 2px solid #757575;
            background: #e0e0e0;
            opacity: 0.8;
          }

          .tower-status-card.under-attack {
            border: 2px solid #f44336;
            background: #ffebee;
          }

          .tower-status-card.defending {
            border: 2px solid #ffcdd2;
            background: #fff5f5;
          }

          .tower-header h4 {
            margin: 0 0 12px 0;
            font-size: 18px;
            color: #333;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          }

          .main-tower-badge {
            background-color: #9e9e9e;
            color: white;
            font-size: 12px;
            padding: 2px 8px;
            border-radius: 4px;
            font-weight: bold;
          }

          .tower-attacks {
            font-size: 24px;
            font-weight: bold;
            color: #4a90e2;
          }

          .attack-count {
            display: block;
          }

          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }

          .modal-content {
            background: white;
            border-radius: 12px;
            padding: 24px;
            max-width: 500px;
            width: 90%;
            max-height: 80vh;
            overflow-y: auto;
            box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #f0f0f0;
            padding-bottom: 12px;
          }

          .modal-header h3 {
            margin: 0;
            font-size: 20px;
            color: #333;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .close-button {
            background: none;
            border: none;
            font-size: 28px;
            cursor: pointer;
            color: #666;
            padding: 0;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            transition: background-color 0.2s;
          }

          .close-button:hover {
            background-color: #f0f0f0;
          }

          .defenders-list-modal {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .no-defenders {
            text-align: center;
            color: #999;
            padding: 20px;
            font-size: 14px;
          }

          .defender-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px;
            background: #f9f9f9;
            border-radius: 8px;
            transition: background-color 0.2s;
          }

          .defender-item:hover {
            background-color: #f0f0f0;
          }

          .defender-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .defender-name {
            font-weight: 600;
            font-size: 16px;
            color: #333;
          }

          .defender-league {
            font-size: 12px;
            color: #666;
            background: #e0e0e0;
            padding: 2px 8px;
            border-radius: 4px;
            display: inline-block;
            width: fit-content;
          }

          .defender-stats {
            display: flex;
            flex-direction: column;
            gap: 4px;
            align-items: flex-end;
          }


          .win-count {
            font-size: 20px;
            color: #28a745;
            font-weight: 700;
          }
        `}</style>
      </div>
    );
  };

  // 攻撃状況タブの内容
  const AttackStatusTab = () => {
    return (
      <div className="attack-status-tab">
        <div className="subtab-navigation-inner">
          <button
            className={`subtab-button ${attackStatusView === 'ally' ? 'active' : ''}`}
            onClick={() => setAttackStatusView('ally')}
          >
            味方
          </button>
          <button
            className={`subtab-button ${attackStatusView === 'enemy' ? 'active' : ''}`}
            onClick={() => setAttackStatusView('enemy')}
          >
            敵
          </button>
        </div>
        {attackStatusView === 'ally' ? <AllyAttackContent /> : <EnemyAttackContent />}
      </div>
    );
  };

  // 味方の攻撃状況コンテンツ
  const AllyAttackContent = () => {
    const [expandedSections, setExpandedSections] = useState({
      completed: false,
      partial: false,
      pending: false
    });

    // セクションの展開/折りたたみを切り替える
    const toggleSection = (section) => {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    // 味方の状態を取得する関数
    const fetchAllyStatus = async () => {
      if (!authToken) return;

      try {
        setUpdateMessage('データを更新中...');

        // まずcontributionを取得
        await axios.get(
          `${API_BASE_URL}/api/guild-war-instructions-nihon/contribution/d1431c50f9d963b8d66f195b156e7db8`,
          {
            headers: { Authorization: `Bearer ${authToken}` }
          }
        );

        // その後、選択された戦闘のattack-statusを取得
        const response = await axios.get(
          `${API_BASE_URL}/api/guild-war-instructions-nihon/attack-status/d1431c50f9d963b8d66f195b156e7db8/${selectedBattle}`,
          {
            headers: { Authorization: `Bearer ${authToken}` }
          }
        );

        if (response.data) {
          setAllyStatus(prev => ({
            ...prev,
            [selectedBattle]: response.data
          }));
          setUpdateMessage('✅ 更新完了');
          setTimeout(() => setUpdateMessage(''), 3000);
        }
      } catch (error) {
        console.error('データ取得に失敗:', error);
        setUpdateMessage('❌ 更新失敗');
        setTimeout(() => setUpdateMessage(''), 3000);
      }
    };

    // メンバーの攻撃状況を表示するコンポーネント
    // memberはオブジェクト { name, winCount, loseCount } または文字列
    const MemberStatus = memo(({ member, status }) => {
      const getStatusColor = () => {
        switch (status) {
          case 'completed': return 'rgb(76, 175, 80, 0.1)';
          case 'partial': return 'rgb(255, 152, 0, 0.1)';
          case 'pending': return 'rgb(244, 67, 54, 0.1)';
          default: return 'transparent';
        }
      };

      // オブジェクト形式と文字列の両方に対応
      const isObject = typeof member === 'object' && member !== null;
      const name = isObject ? member.name : member;
      const winCount = isObject ? member.winCount : 0;
      const loseCount = isObject ? member.loseCount : 0;
      const maxBattles = 2; // 最大攻撃回数
      const winPercent = (winCount / maxBattles) * 100;
      const losePercent = (loseCount / maxBattles) * 100;

      return (
        <div className="member-status" style={{ backgroundColor: getStatusColor() }}>
          <div className="member-info">
            <div className="member-name">{name}</div>
            {isObject && (
              <div className="win-rate-container">
                <div className="win-rate-bar" style={{ backgroundColor: '#e0e0e0' }}>
                  <div className="win-rate-stacked">
                    <div
                      className="win-rate-segment win"
                      style={{ width: `${winPercent}%` }}
                    />
                    <div
                      className="win-rate-segment lose"
                      style={{ width: `${losePercent}%` }}
                    />
                  </div>
                </div>
                <span className="win-rate-text">{winCount}W-{loseCount}L</span>
              </div>
            )}
          </div>
        </div>
      );
    });

    // ギルド全体の統計を計算
    const calculateGuildStats = () => {
      const currentStatus = allyStatus[selectedBattle];
      if (!currentStatus) {
        return { totalWins: 0, totalLosses: 0, totalNoAttacks: 0, totalMembers: 0 };
      }

      let totalWins = 0;
      let totalLosses = 0;
      let totalMembers = 0;

      // 完了メンバー (2/2)
      if (currentStatus.completed) {
        currentStatus.completed.forEach(member => {
          totalWins += member.winCount || 0;
          totalLosses += member.loseCount || 0;
          totalMembers++;
        });
      }

      // 一部攻撃メンバー (1/2)
      if (currentStatus.partialAttack) {
        currentStatus.partialAttack.forEach(member => {
          totalWins += member.winCount || 0;
          totalLosses += member.loseCount || 0;
          totalMembers++;
        });
      }

      // 未攻撃メンバー (0/2)
      if (currentStatus.pending) {
        totalMembers += currentStatus.pending.length;
      }

      const maxAttacks = totalMembers * 2;
      const totalNoAttacks = maxAttacks - totalWins - totalLosses;

      return { totalWins, totalLosses, totalNoAttacks, totalMembers, maxAttacks };
    };

    const guildStats = calculateGuildStats();
    const winPercent = guildStats.maxAttacks > 0 ? (guildStats.totalWins / guildStats.maxAttacks) * 100 : 0;
    const losePercent = guildStats.maxAttacks > 0 ? (guildStats.totalLosses / guildStats.maxAttacks) * 100 : 0;
    const noAttackPercent = guildStats.maxAttacks > 0 ? (guildStats.totalNoAttacks / guildStats.maxAttacks) * 100 : 0;

    return (
      <div className="ally-tab">
        <div className="controls">
          <button onClick={fetchAllyStatus} className="refresh-button">
            情報更新
          </button>
          {updateMessage && (
            <p className={`update-message ${updateMessage.includes('✅') ? 'success' : 'error'}`}>
              {updateMessage}
            </p>
          )}
        </div>

        {/* ギルド全体の統計 */}
        <div className="guild-stats-container">
          <h3 className="guild-stats-title">ギルド全体の統計</h3>
          <div className="guild-stats-card">
            <div className="guild-stats-bar-container">
              <div className="guild-stats-bar">
                <div className="guild-stats-stacked">
                  <div
                    className="guild-stats-segment win"
                    style={{ width: `${winPercent}%` }}
                    title={`勝利数: ${guildStats.totalWins}`}
                  />
                  <div
                    className="guild-stats-segment lose"
                    style={{ width: `${losePercent}%` }}
                    title={`敗北数: ${guildStats.totalLosses}`}
                  />
                  <div
                    className="guild-stats-segment no-attack"
                    style={{ width: `${noAttackPercent}%` }}
                    title={`未攻撃: ${guildStats.totalNoAttacks}`}
                  />
                </div>
              </div>
            </div>
            <div className="guild-stats-details">
              <div className="stat-item win">
                <span className="stat-label">勝利数</span>
                <span className="stat-value">{guildStats.totalWins}</span>
              </div>
              <div className="stat-item lose">
                <span className="stat-label">敗北数</span>
                <span className="stat-value">{guildStats.totalLosses}</span>
              </div>
              <div className="stat-item no-attack">
                <span className="stat-label">未攻撃</span>
                <span className="stat-value">{guildStats.totalNoAttacks}</span>
              </div>
              <div className="stat-item total">
                <span className="stat-label">合計</span>
                <span className="stat-value">{guildStats.maxAttacks}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="battle-section">
          <div className="battle-status">
            <div className="status-column">
              <div className="status-header" onClick={() => toggleSection('completed')}>
                <h4>攻撃済み（2回完了） ({allyStatus[selectedBattle]?.completed?.length || 0}人)</h4>
                <span className="toggle-icon">{expandedSections.completed ? '▼' : '▶'}</span>
              </div>
              {expandedSections.completed && (
                <ul className="status-list completed">
                  {allyStatus[selectedBattle]?.completed?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="completed" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="status-column">
              <div className="status-header" onClick={() => toggleSection('partial')}>
                <h4>1回のみ攻撃 ({allyStatus[selectedBattle]?.partialAttack?.length || 0}人)</h4>
                <span className="toggle-icon">{expandedSections.partial ? '▼' : '▶'}</span>
              </div>
              {expandedSections.partial && (
                <ul className="status-list partial">
                  {allyStatus[selectedBattle]?.partialAttack?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="partial" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="status-column">
              <div className="status-header" onClick={() => toggleSection('pending')}>
                <h4>未攻撃 ({allyStatus[selectedBattle]?.pending?.length || 0}人)</h4>
                <span className="toggle-icon">{expandedSections.pending ? '▼' : '▶'}</span>
              </div>
              {expandedSections.pending && (
                <ul className="status-list pending">
                  {allyStatus[selectedBattle]?.pending?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="pending" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 敵の攻撃状況コンテンツ
  const EnemyAttackContent = () => {
    const [updateMessage, setUpdateMessage] = useState('');
    const [expandedSections, setExpandedSections] = useState({
      completed: false,
      partial: false,
      pending: false
    });

    const toggleSection = (section) => {
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    const fetchEnemyAttackStatus = async () => {
      try {
        setUpdateMessage('敵の攻撃状況を更新中...');

        // まず敵ギルドのgidを取得
        const response = await axios.get(
          `${API_BASE_URL}/api/guild-war-instructions-nihon/enemy-towers?battleNumber=${selectedBattle}`,
          {
            headers: { Authorization: `Bearer ${authToken}` }
          }
        );

        const gid = response.data.gid;

        if (gid) {
          // まずcontributionを取得
          await axios.get(
            `${API_BASE_URL}/api/guild-war-instructions-nihon/contribution/${gid}`,
            {
              headers: { Authorization: `Bearer ${authToken}` }
            }
          );

          // その後、attack-statusを取得
          const attackStatusResponse = await axios.get(
            `${API_BASE_URL}/api/guild-war-instructions-nihon/attack-status/${gid}/${selectedBattle}`,
            {
              headers: { Authorization: `Bearer ${authToken}` }
            }
          );

          if (attackStatusResponse.data) {
            setEnemyAttackStatus(prev => ({
              ...prev,
              [selectedBattle]: attackStatusResponse.data
            }));
            setUpdateMessage('✅ 更新完了');
          } else {
            setUpdateMessage('❌ 攻撃状況の取得に失敗しました');
          }
        } else {
          setUpdateMessage('❌ 敵ギルドのIDが見つかりません');
        }

        setTimeout(() => setUpdateMessage(''), 3000);
      } catch (error) {
        console.error('敵の攻撃状況の更新に失敗:', error);
        setUpdateMessage('❌ 更新失敗');
        setTimeout(() => setUpdateMessage(''), 3000);
      }
    };

    // memberはオブジェクト { name, winCount, loseCount } または文字列
    const MemberStatus = memo(({ member, status }) => {
      const getStatusColor = () => {
        switch (status) {
          case 'completed': return 'rgba(76, 175, 80, 0.1)';
          case 'partial': return 'rgba(255, 152, 0, 0.1)';
          case 'pending': return 'rgba(244, 67, 54, 0.1)';
          default: return 'transparent';
        }
      };

      // オブジェクト形式と文字列の両方に対応
      const isObject = typeof member === 'object' && member !== null;
      const name = isObject ? member.name : member;
      const winCount = isObject ? member.winCount : 0;
      const loseCount = isObject ? member.loseCount : 0;
      const maxBattles = 2; // 最大攻撃回数
      const winPercent = (winCount / maxBattles) * 100;
      const losePercent = (loseCount / maxBattles) * 100;

      return (
        <div className="member-status" style={{ backgroundColor: getStatusColor() }}>
          <div className="member-info">
            <div className="member-name">{name}</div>
            {isObject && (
              <div className="win-rate-container">
                <div className="win-rate-bar" style={{ backgroundColor: '#e0e0e0' }}>
                  <div className="win-rate-stacked">
                    <div
                      className="win-rate-segment win"
                      style={{ width: `${winPercent}%` }}
                    />
                    <div
                      className="win-rate-segment lose"
                      style={{ width: `${losePercent}%` }}
                    />
                  </div>
                </div>
                <span className="win-rate-text">{winCount}W-{loseCount}L</span>
              </div>
            )}
          </div>
        </div>
      );
    });

    // ギルド全体の統計を計算
    const calculateGuildStats = () => {
      const currentStatus = enemyAttackStatus[selectedBattle];
      if (!currentStatus) {
        return { totalWins: 0, totalLosses: 0, totalNoAttacks: 0, totalMembers: 0 };
      }

      let totalWins = 0;
      let totalLosses = 0;
      let totalMembers = 0;

      // 完了メンバー (2/2)
      if (currentStatus.completed) {
        currentStatus.completed.forEach(member => {
          totalWins += member.winCount || 0;
          totalLosses += member.loseCount || 0;
          totalMembers++;
        });
      }

      // 一部攻撃メンバー (1/2)
      if (currentStatus.partialAttack) {
        currentStatus.partialAttack.forEach(member => {
          totalWins += member.winCount || 0;
          totalLosses += member.loseCount || 0;
          totalMembers++;
        });
      }

      // 未攻撃メンバー (0/2)
      if (currentStatus.pending) {
        totalMembers += currentStatus.pending.length;
      }

      const maxAttacks = totalMembers * 2;
      const totalNoAttacks = maxAttacks - totalWins - totalLosses;

      return { totalWins, totalLosses, totalNoAttacks, totalMembers, maxAttacks };
    };

    const guildStats = calculateGuildStats();
    const winPercent = guildStats.maxAttacks > 0 ? (guildStats.totalWins / guildStats.maxAttacks) * 100 : 0;
    const losePercent = guildStats.maxAttacks > 0 ? (guildStats.totalLosses / guildStats.maxAttacks) * 100 : 0;
    const noAttackPercent = guildStats.maxAttacks > 0 ? (guildStats.totalNoAttacks / guildStats.maxAttacks) * 100 : 0;

    return (
      <div className="enemy-attack-tab">
        <div className="controls">
          <button onClick={fetchEnemyAttackStatus} className="refresh-button">
            情報更新
          </button>
          {updateMessage && (
            <p className={`update-message ${updateMessage.includes('✅') ? 'success' : 'error'}`}>
              {updateMessage}
            </p>
          )}
        </div>

        {/* ギルド全体の統計 */}
        <div className="guild-stats-container">
          <h3 className="guild-stats-title">ギルド全体の統計</h3>
          <div className="guild-stats-card">
            <div className="guild-stats-bar-container">
              <div className="guild-stats-bar">
                <div className="guild-stats-stacked">
                  <div
                    className="guild-stats-segment win"
                    style={{ width: `${winPercent}%` }}
                    title={`勝利数: ${guildStats.totalWins}`}
                  />
                  <div
                    className="guild-stats-segment lose"
                    style={{ width: `${losePercent}%` }}
                    title={`敗北数: ${guildStats.totalLosses}`}
                  />
                  <div
                    className="guild-stats-segment no-attack"
                    style={{ width: `${noAttackPercent}%` }}
                    title={`未攻撃: ${guildStats.totalNoAttacks}`}
                  />
                </div>
              </div>
            </div>
            <div className="guild-stats-details">
              <div className="stat-item win">
                <span className="stat-label">勝利数</span>
                <span className="stat-value">{guildStats.totalWins}</span>
              </div>
              <div className="stat-item lose">
                <span className="stat-label">敗北数</span>
                <span className="stat-value">{guildStats.totalLosses}</span>
              </div>
              <div className="stat-item no-attack">
                <span className="stat-label">未攻撃</span>
                <span className="stat-value">{guildStats.totalNoAttacks}</span>
              </div>
              <div className="stat-item total">
                <span className="stat-label">合計</span>
                <span className="stat-value">{guildStats.maxAttacks}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="battle-section">
          <div className="battle-status">
            <div className="status-column">
              <div className="status-header" onClick={() => toggleSection('completed')}>
                <h4>攻撃済み（2回完了） ({enemyAttackStatus[selectedBattle]?.completed?.length || 0}人)</h4>
                <span className="toggle-icon">{expandedSections.completed ? '▼' : '▶'}</span>
              </div>
              {expandedSections.completed && (
                <ul className="status-list completed">
                  {enemyAttackStatus[selectedBattle]?.completed?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="completed" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="status-column">
              <div className="status-header" onClick={() => toggleSection('partial')}>
                <h4>1回のみ攻撃 ({enemyAttackStatus[selectedBattle]?.partialAttack?.length || 0}人)</h4>
                <span className="toggle-icon">{expandedSections.partial ? '▼' : '▶'}</span>
              </div>
              {expandedSections.partial && (
                <ul className="status-list partial">
                  {enemyAttackStatus[selectedBattle]?.partialAttack?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="partial" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="status-column">
              <div className="status-header" onClick={() => toggleSection('pending')}>
                <h4>未攻撃 ({enemyAttackStatus[selectedBattle]?.pending?.length || 0}人)</h4>
                <span className="toggle-icon">{expandedSections.pending ? '▼' : '▶'}</span>
              </div>
              {expandedSections.pending && (
                <ul className="status-list pending">
                  {enemyAttackStatus[selectedBattle]?.pending?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="pending" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div>読み込み中...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-form">
        <form onSubmit={handleLogin}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="パスワードを入力"
          />
          <button type="submit">ログイン</button>
          {authError && <p className="error">{authError}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="guild-war-instructions">
      <BattleSelector />
      <TabNavigation />
      <div className="tab-content">
        {activeTab === 'instructionManagement' && (
          <>
            {instructionSubTab === 'instructions' && (
              <>
                <InstructionsTab />
                <DeleteSection />
              </>
            )}
            {instructionSubTab === 'enemy' && <EnemyTab />}
          </>
        )}
        {activeTab === 'battleStatus' && (
          <>
            {battleStatusSubTab === 'attackStatus' && <AttackStatusTab />}
            {battleStatusSubTab === 'battleMap' && <BattleStatusTab />}
          </>
        )}
      </div>
    </div>
  );
};

export default GuildWarInstructions;
